import { create } from 'zustand';
import { persist } from 'zustand/middleware';

type ThemeState = {
  theme: 'light' | 'dark' | 'system';
  setTheme: (theme: 'light' | 'dark' | 'system') => void;
  resolvedTheme: 'light' | 'dark';
};

const getSystemTheme = (): 'light' | 'dark' => {
  return window.matchMedia('(prefers-color-scheme: dark)').matches 
    ? 'dark' 
    : 'light';
};

export const useThemeStore = create<ThemeState>()(
  persist(
    (set) => ({
      theme: 'system',
      resolvedTheme: getSystemTheme(),
      setTheme: (theme) => {
        const resolved = theme === 'system' ? getSystemTheme() : theme;
        set({ theme, resolvedTheme: resolved });
        document.documentElement.classList.toggle('dark', resolved === 'dark');
      },
    }),
    {
      name: 'theme-store',
      onRehydrateStorage: () => (state) => {
        if (state) {
          const resolved = state.theme === 'system'
            ? getSystemTheme()
            : state.theme;
          document.documentElement.classList.toggle('dark', resolved === 'dark');
        }
      },
    }
  )
);

// Initialize theme on store creation
const initializeTheme = () => {
  const store = useThemeStore.getState();
  const resolved = store.theme === 'system' 
    ? getSystemTheme() 
    : store.theme;
  document.documentElement.classList.toggle('dark', resolved === 'dark');
};

initializeTheme();

// Listen for system theme changes
window.matchMedia('(prefers-color-scheme: dark)').addEventListener('change', e => {
  const store = useThemeStore.getState();
  if (store.theme === 'system') {
    const resolved = e.matches ? 'dark' : 'light';
    store.setTheme('system');
    document.documentElement.classList.toggle('dark', resolved === 'dark');
  }
});